import type { DesktopAgentBridge } from './desktop-agent-transport';

type Scope = { accountId: number; taskId: number; runId: string };
type Identity = { accountId: number | null; taskId: number | null };
type Registration = { bridge: DesktopAgentBridge; identity: () => Identity };
type ToolRequest = { runId: string; requestId: string; token: string; toolName: string; input: unknown };
const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const toolNames = new Set(['local_read', 'local_write', 'local_edit', 'local_glob', 'local_grep', 'local_bash']);
const failure = () => ({ success: false, content: 'The local tool did not complete.' });
let registration: Registration | null = null;
const turns = new Set<DesktopRuntimeTurn>();

export function registerDesktopRuntime(bridge: DesktopAgentBridge, identity: () => Identity) {
  const owner = { bridge, identity };
  registration = owner;
  reconcileDesktopRuntime();
  return () => {
    if (registration === owner) registration = null;
    for (const turn of Array.from(turns)) if (turn.owner === owner) void turn.end();
  };
}

/** Navigation stops local capabilities; an account change also closes its old consumer stream. */
export function reconcileDesktopRuntime() {
  for (const turn of Array.from(turns)) {
    const current = registration?.identity() ?? turn.owner.identity();
    if (current.accountId !== turn.scope.accountId || current.taskId !== turn.scope.taskId) {
      if (current.accountId != null && current.accountId !== turn.scope.accountId) turn.abort.abort();
      void turn.end();
    }
  }
}

export function stopDesktopRuntime(accountId: number | null, taskId: number | null) {
  for (const turn of Array.from(turns)) if (turn.scope.accountId === accountId && turn.scope.taskId === taskId) {
    turn.abort.abort(); void turn.end();
  }
}

export class DesktopRuntimeTurn {
  closed = false;
  private ending?: Promise<unknown>;
  private requests = new Map<string, string>();
  constructor(readonly owner: Registration, readonly scope: Scope, readonly abort: AbortController, private fetcher: typeof fetch) {
    turns.add(this);
    abort.signal.addEventListener('abort', () => { void this.end(); }, { once: true });
  }
  headers() { return { 'X-Admoji-Account-Id': String(this.scope.accountId), 'X-Admoji-Task-Id': String(this.scope.taskId) }; }
  private mayExecute() {
    const current = this.owner.identity();
    return !this.closed && !this.abort.signal.aborted && registration === this.owner && current.accountId === this.scope.accountId && current.taskId === this.scope.taskId;
  }
  async handle(value: unknown) {
    const request = value as ToolRequest;
    if (!request || request.runId !== this.scope.runId || !uuid.test(request.requestId ?? '') || typeof request.token !== 'string' || !/^[A-Za-z0-9_-]{32,128}$/.test(request.token) || !toolNames.has(request.toolName)) throw Error('Invalid desktop tool request.');
    const digest = JSON.stringify({ token: request.token, toolName: request.toolName, input: request.input });
    if (this.requests.has(request.requestId)) {
      if (this.requests.get(request.requestId) !== digest) throw Error('A desktop request identity was reused with different input.');
      return;
    }
    if (this.requests.size >= 20) throw Error('The desktop tool limit was reached.');
    this.requests.set(request.requestId, digest);
    let result = failure();
    if (this.mayExecute()) {
      try {
        if (new TextEncoder().encode(JSON.stringify(request.input)).length > 32768) throw Error();
        const native = await this.owner.bridge.executeTool({ ...this.scope, requestId: request.requestId, toolName: request.toolName, input: request.input });
        if (this.mayExecute() && typeof native?.success === 'boolean' && typeof native.content === 'string' && new TextEncoder().encode(JSON.stringify(native)).length <= 256 * 1024) result = { success: native.success, content: native.success ? native.content : failure().content };
      } catch { /* Raw local errors and file contents never enter UI history. */ }
    }
    if ((registration?.identity() ?? this.owner.identity()).accountId !== this.scope.accountId || this.abort.signal.aborted) throw new DOMException('Desktop turn stopped.', 'AbortError');
    // One token-bound result delivery, without automatic retries of local actions.
    const response = await this.fetcher('/api/canvas/desktop-tool-result', {
      method: 'POST', credentials: 'include', cache: 'no-store', signal: this.abort.signal,
      headers: { 'Content-Type': 'application/json', ...this.headers() },
      body: JSON.stringify({ projectId: this.scope.taskId, runId: this.scope.runId, requestId: request.requestId, token: request.token, result }),
    });
    if (!response.ok) throw Object.assign(Error('The desktop tool result could not be delivered.'), { code: 'DESKTOP_TOOL_RESULT_UNDELIVERED' });
  }
  end() {
    if (this.ending) return this.ending;
    this.closed = true;
    this.ending = Promise.resolve().then(() => this.owner.bridge.endTurn(this.scope)).catch(() => {});
    return this.ending;
  }
  async dispose() { await this.end(); turns.delete(this); }
}

export async function beginDesktopRuntimeTurn(taskId: number | null, abort: AbortController, fetcher: typeof fetch = globalThis.fetch.bind(globalThis)): Promise<DesktopRuntimeTurn | null> {
  const owner = registration;
  if (!owner || !Number.isSafeInteger(taskId) || taskId! < 1) return null;
  const identity = owner.identity();
  if (!Number.isSafeInteger(identity.accountId) || identity.accountId! < 1 || identity.taskId !== taskId) return null;
  const accountId = identity.accountId!;
  const valid = () => registration === owner && owner.identity().accountId === accountId && owner.identity().taskId === taskId && !abort.signal.aborted;
  let runId: string | undefined;
  try {
    const state = await owner.bridge.getState();
    if (!valid()) throw new DOMException('Desktop startup scope changed.', 'AbortError');
    if (state.accountId !== accountId || !state.signedIn || !state.workspace || state.running) return null;
    ({ runId } = await owner.bridge.beginTurn({ accountId, taskId: taskId! }));
    if (!uuid.test(runId)) throw Error();
    if (!valid()) throw new DOMException('Desktop startup scope changed.', 'AbortError');
    return new DesktopRuntimeTurn(owner, { accountId, taskId: taskId!, runId }, abort, fetcher);
  } catch {
    if (runId) await owner.bridge.endTurn({ accountId, taskId: taskId!, runId }).catch(() => {});
    if (!valid()) {
      // No cloud request has started yet: navigation must cancel admission rather
      // than silently downgrade this captured request to a web-only paid turn.
      abort.abort();
      throw new DOMException('Desktop startup scope changed.', 'AbortError');
    }
    throw Object.assign(Error('Desktop local tools could not start. Reopen the app and try again.'), { code: 'DESKTOP_RUNTIME_UNAVAILABLE' });
  }
}
